import React, { useMemo } from 'react';
import {
    ACCOUNT_TYPES,
    ACCOUNT_TYPE_LABELS,
    ACCOUNT_TYPE_BADGES,
    inferAccountType
} from '../../constants';
import { formatCurrency, formatDate, getFiscalYearRange } from '../../utils';

const TrialBalance = ({ accounts, calculateBalances, currentFiscalYear }) => {
    const trialBalanceData = useMemo(() => {
        const data = [];
        let totalDebit = 0;
        let totalCredit = 0;

        const sortedAccounts = [...accounts].sort((a, b) => {
            const typeOrder = { asset: 1, liability: 2, equity: 3, revenue: 4, expense: 5 };
            if (typeOrder[a.type] !== typeOrder[b.type]) {
                return typeOrder[a.type] - typeOrder[b.type];
            }
            return a.code.localeCompare(b.code);
        });

        sortedAccounts.forEach(account => {
            const balance = calculateBalances[account.id] || { debit: 0, credit: 0 };
            if (balance.debit === 0 && balance.credit === 0) return;

            let debitBalance = 0;
            let creditBalance = 0;

            if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                const net = balance.debit - balance.credit;
                if (net > 0) debitBalance = net;
                else creditBalance = -net;
            } else {
                const net = balance.credit - balance.debit;
                if (net > 0) creditBalance = net;
                else debitBalance = -net;
            }

            totalDebit += debitBalance;
            totalCredit += creditBalance;

            data.push({
                account,
                debit: balance.debit,
                credit: balance.credit,
                debitBalance,
                creditBalance
            });
        });

        return { data, totalDebit, totalCredit };
    }, [accounts, calculateBalances]);

    const { start, end } = getFiscalYearRange(currentFiscalYear);

    return (
        <div>
            <div className="page-header">
                <h2>試算表</h2>
            </div>

            <div className="card">
                <h3 className="card-title">
                    合計残高試算表
                    <span style={{fontSize: '14px', fontWeight: 'normal', marginLeft: '20px', color: '#666'}}>
                        {currentFiscalYear}年度（{formatDate(start)}〜{formatDate(end)}）
                    </span>
                </h3>

                {trialBalanceData.data.length > 0 ? (
                    <table>
                        <thead>
                            <tr>
                                <th>勘定科目</th>
                                <th>区分</th>
                                <th className="text-right">借方合計</th>
                                <th className="text-right">貸方合計</th>
                                <th className="text-right">借方残高</th>
                                <th className="text-right">貸方残高</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trialBalanceData.data.map(row => (
                                <tr key={row.account.id} title={row.account.description || ''}>
                                    <td>{row.account.code} {row.account.name}</td>
                                    <td>
                                        <span className={`badge ${ACCOUNT_TYPE_BADGES[row.account.type]}`}>
                                            {ACCOUNT_TYPE_LABELS[row.account.type]}
                                        </span>
                                    </td>
                                    <td className="text-right amount">¥{formatCurrency(row.debit)}</td>
                                    <td className="text-right amount">¥{formatCurrency(row.credit)}</td>
                                    <td className="text-right amount">
                                        {row.debitBalance > 0 ? `¥${formatCurrency(row.debitBalance)}` : ''}
                                    </td>
                                    <td className="text-right amount">
                                        {row.creditBalance > 0 ? `¥${formatCurrency(row.creditBalance)}` : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{fontWeight: 'bold', borderTop: '2px solid #333'}}>
                                <td colSpan="4" className="text-right">合計</td>
                                <td className="text-right amount">¥{formatCurrency(trialBalanceData.totalDebit)}</td>
                                <td className="text-right amount">¥{formatCurrency(trialBalanceData.totalCredit)}</td>
                            </tr>
                        </tfoot>
                    </table>
                ) : (
                    <div className="empty-state">
                        <h3>データがありません</h3>
                        <p>仕訳を登録すると試算表が自動生成されます</p>
                    </div>
                )}

                {trialBalanceData.totalDebit !== trialBalanceData.totalCredit && trialBalanceData.data.length > 0 && (
                    <div className="alert alert-error" style={{marginTop: '20px'}}>
                        貸借が一致していません。仕訳を確認してください。
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrialBalance;
